import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import './App.css'; // REMOVED
import { ThemeProvider } from 'styled-components'; // NEW
import { theme } from './styles/theme'; // NEW
import { GlobalStyles } from './styles/GlobalStyles'; // NEW
import { ReactLenis } from 'lenis/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { Helmet } from 'react-helmet-async';
import { BRAND_NAME, BRAND_URL, BRAND_YOUTUBE_URL, BRAND_GITHUB_URL, BRAND_TWITTER_URL, BRAND_INSTAGRAM_URL } from './constants/brand';
import MainPageLayout from './components/MainPageLayout';
import PageLayout from './components/PageLayout';
import Footer from './components/Footer';
import Background from './components/Background';
import DotLoader from './components/DotLoader';
import { getLanguageFromCountry } from './utils/getLanguageFromCountry';

import styled from 'styled-components'; // NEW

const LegalPage = lazy(() => import('./pages/LegalPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const HirePage = lazy(() => import('./pages/HirePage'));
const supportedLanguages = ['en', 'ar', 'es', 'zh', 'hi', 'ja'];

const BackgroundWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const PageWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: ${props => props.theme.colors.white};
  font-size: 1.2rem;
  min-height: 120px;
`;

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const applyLanguage = (language) => {
      i18n.changeLanguage(language);
      document.documentElement.lang = language;
      document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    };

    const checkIpLocation = async () => {
      const languagePreference = localStorage.getItem('languagePreference');
      const hasChecked = localStorage.getItem('hasCheckedIpLocation');
      const detectedLanguage = localStorage.getItem('detectedLanguage');

      // An explicit saved preference always wins, including across reloads.
      if (supportedLanguages.includes(languagePreference)) {
        applyLanguage(languagePreference);
      } else if (!supportedLanguages.includes(languagePreference) && supportedLanguages.includes(detectedLanguage)) {
        applyLanguage(detectedLanguage);
      }

      if ((!hasChecked || !detectedLanguage) && !supportedLanguages.includes(languagePreference)) {
        try {
          const providers = [
            {
              url: 'https://ipwho.is/',
              getCountryCode: data => data.success && data.country_code,
              getCountryName: data => data.country
            },
            {
              url: 'https://ipinfo.io/json',
              getCountryCode: data => data.country,
              getCountryName: data => data.country
            }
          ];

          for (const provider of providers) {
            const response = await fetch(provider.url);
            if (!response.ok) continue;

            const data = await response.json();
            const countryCode = provider.getCountryCode(data);
            if (!countryCode) continue;

            const detectedLang = getLanguageFromCountry(countryCode) || 'en';
            const location = provider.getCountryName(data);

            localStorage.setItem('detectedLanguage', detectedLang);
            applyLanguage(detectedLang);

            if (location) {
              localStorage.setItem('detectedLocation', location);
              window.dispatchEvent(new CustomEvent('language-detected', {
                detail: { location }
              }));
            }

            break;
          }
        } catch (error) {
          console.error('Error detecting IP location:', error);
        } finally {
          if (!localStorage.getItem('detectedLanguage')) {
            localStorage.setItem('detectedLanguage', 'en');
            applyLanguage('en');
          }
          // Mark as checked so we don't fetch on every reload
          localStorage.setItem('hasCheckedIpLocation', 'true');
        }
      }
    };

    checkIpLocation();

    window.addEventListener('language-auto-selected', checkIpLocation);
    return () => window.removeEventListener('language-auto-selected', checkIpLocation);
  }, [i18n]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Helmet>
        <title>{`Developer & Content Creator | ${BRAND_NAME}`}</title>
        <meta name="description" content="Developer and content creator showcasing projects, open-source work, and creative content." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": BRAND_NAME,
            "url": BRAND_URL,
            "sameAs": [
              BRAND_YOUTUBE_URL,
              BRAND_TWITTER_URL,
              BRAND_INSTAGRAM_URL,
              BRAND_GITHUB_URL
            ],
            "jobTitle": ["Developer", "Content Creator"],
            "knowsAbout": ["Web Development", "Front-End Development", "Back-End Development", "Android Development", "Content Creation", "Open Source Software"],
            "image": `${BRAND_URL}/assets/images/logo.webp`,
            "description": "Developer and content creator showcasing projects, open-source work, and creative content."
          })}
        </script>
      </Helmet>
      <ReactLenis root>
        <SpeedInsights />
        <Analytics />
        <BackgroundWrapper>
          <Background
            hueShift={240}
            warpAmount={0.1}
            noiseIntensity={0.02}
            scanlineIntensity={0.05}
            resolutionScale={0.7}
          />
        </BackgroundWrapper>
        <PageWrapper>
          <Router>
            <Suspense fallback={<LoadingFallback><DotLoader /></LoadingFallback>}>
              <Routes>
                <Route path="/" element={<MainPageLayout />} />

                <Route element={<PageLayout />}>
                  <Route path="/hire" element={<HirePage />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/coming-soon" element={<ComingSoonPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <Footer />
          </Router>
        </PageWrapper>
      </ReactLenis>
    </ThemeProvider>
  );
}

export default App;